import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { notification } from 'antd';
import Flex from '../../../../components/Flex';
import { makePrioStyles } from '../../../../theme/utils';
import { Message } from '../../../../models/Message';
import { debounceFunction, sanitizeHTML } from '../../../../util';
import ReactHtmlParser from 'react-html-parser';
import { DomElement } from 'htmlparser2';
import useOverflowDetection from '../../../../hooks/useOverflowDetection';
import { apiCreateMessageDraft, apiUpdateMessageDraft } from '../../api';
import { useTranslation } from 'react-i18next';
import { ProjectId } from '../../../../models/Types';
import { parseMailto, ParsedMailto, escapeHtml } from '../../util';

const linkRegex = /^((?:[a-z]+:)?\/\/|mailto:|file:)/;

const useStyles = makePrioStyles((theme) => ({
  root: {},
  compressAffix: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    '& .ant-affix': {
      marginTop: theme.old.spacing.defaultPadding,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
    },
  },
  compressButton: {
    height: 32,
    width: 32,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.old.typography.colors.muted,
    borderRadius: 2,
    transition: 'background-color 0s',
    boxShadow:
      '0 3px 6px -4px rgb(0 0 0 / 12%), 0 6px 16px 0 rgb(0 0 0 / 8%), 0 9px 28px 8px rgb(0 0 0 / 5%)',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.85)',
      '& svg': {
        color: theme.old.palette.backgroundPalette.content,
      },
    },
  },
  compressIcon: {
    transition: 'color 0.2s',
  },
}));

const findBodyTagChildren: (nodes: DomElement[]) => DomElement[] = (
  nodes: DomElement[]
) => {
  let result: DomElement[];
  for (const node of nodes) {
    if (node.type === 'tag' && node.name === 'body') {
      result = node.children;
      break;
    }
    if (node.children) {
      const bodyTagChildren = findBodyTagChildren(node.children);
      if (bodyTagChildren) {
        result = bodyTagChildren;
        break;
      }
    }
  }
  return result;
};
const manipulateDom: (
  nodes: DomElement[],
  maxWidth: number | undefined
) => DomElement[] = (nodes, maxWidth) => {
  return nodes?.map((node) => {
    if (node.type === 'style' && node.name === 'style') {
      return {
        ...node,
        type: 'comment',
        ...(node.children
          ? {
              children: manipulateDom(node.children, maxWidth),
            }
          : {}),
      };
    } else if (node.type === 'tag' && node.name === 'a') {
      return {
        ...node,
        attribs: {
          ...(node.attribs ?? {}),
          rel: node.attribs?.hinternalRef?.match(linkRegex)
            ? 'noopener nointernalReferrer'
            : undefined,
          target: node.attribs?.hinternalRef?.match(linkRegex)
            ? '_blank'
            : undefined,
          hinternalRef: node.attribs?.hinternalRef?.match(linkRegex)
            ? (node.attribs?.hinternalRef ?? undefined)
            : undefined,
          style: node.attribs?.style,
        },
        ...(node.children
          ? {
              children: manipulateDom(node.children, maxWidth),
            }
          : {}),
      };
    } else {
      return {
        ...node,
        ...(node.attribs
          ? {
              attribs: {
                ...node.attribs,
                style: node.attribs?.style,
              },
            }
          : {}),
        ...(node.children
          ? {
              children: manipulateDom(node.children, maxWidth),
            }
          : {}),
      };
    }
  });
};

const escapeRegex = (string: string) =>
  string.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

const applySearch: (nodes: DomElement[], searchTerm: string) => DomElement[] = (
  nodes: DomElement[],
  searchTerm
) => {
  const resultingNodes: DomElement[] = [];
  nodes?.forEach((node) => {
    if (
      node.type === 'text' &&
      typeof node.data === 'string' &&
      node.data.toLocaleLowerCase().includes(searchTerm)
    ) {
      const highlightedNodes: DomElement[] = highlightNode(node, searchTerm);

      resultingNodes.push(...highlightedNodes);
    } else {
      resultingNodes.push({
        ...node,
        ...(node.children
          ? { children: applySearch(node.children, searchTerm) }
          : {}),
      });
    }
  });
  return resultingNodes;
};

const highlightNode: (node: DomElement, searchTerm: string) => DomElement[] = (
  node,
  searchTerm
) => {
  if (node.data.toLocaleLowerCase() === searchTerm) {
    const highlightedNode = {
      type: 'tag',
      name: 'span',
      attribs: {
        style: `background-color: #fff100;`,
      },
      parent: node.parent,
      next: node.next,
      prev: node.prev,
      children: [
        {
          ...node,
          ...(node.children
            ? { children: applySearch(node.children, searchTerm) }
            : {}),
          next: null,
          prev: null,
        },
      ],
    };
    highlightedNode.children[0].parent = highlightedNode;
    return [highlightedNode];
  } else {
    let splitNodes: DomElement[] = (node.data as string)
      .split(RegExp('(' + escapeRegex(searchTerm) + ')', 'i'))
      .filter((text) => text !== '')
      .map((text) => ({
        type: 'text',
        data: text,
        parent: node.parent,
        next: null,
        prev: null,
      }));
    splitNodes.forEach((element, index) => {
      if (index === 0) {
        element.prev = node.prev;
      } else {
        element.prev = splitNodes[index - 1];
      }
      if (index < splitNodes.length - 1) {
        element.next = splitNodes[index + 1];
      } else {
        element.next = node.next;
      }
    });
    const result = splitNodes.flatMap((element) =>
      element.data.toLocaleLowerCase().includes(searchTerm)
        ? highlightNode(element, searchTerm)
        : [element]
    );
    return result;
  }
};

const debouncedScrollWidthChange = debounceFunction(
  (
    width: number,
    offsetWidth: number,
    onScrollWidthChange?: (width: number, offsetWidth: number) => void
  ) => {
    if (onScrollWidthChange) {
      onScrollWidthChange(width, offsetWidth);
    }
  },
  250
);

export interface HtmlTextContentRef {
  getScrollWidth: () => number;
  setScrollPosition: (value: { scrollLeft: number; scrollTop: number }) => void;
}

interface HtmlTextContentProps {
  className?: string;
  message: Message;
  searchKeywords?: string | null;
  projectId: ProjectId;
  onScrollWidthChange?: (scrollWidth: number, offsetWidth: number) => void;
}

export const HtmlTextContent = forwardRef<
  HtmlTextContentRef,
  HtmlTextContentProps
>((props, ref) => {
  //#region ------------------------------ Defaults
  const { className, message, searchKeywords, projectId, onScrollWidthChange } =
    props;
  const classes = useStyles();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [, internalRef, bodyRef, bodyMaxWidth] = useOverflowDetection();
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const openDraftMessage = useCallback(
    async (projectId: string, mailto: ParsedMailto) => {
      const { data: message } = await apiCreateMessageDraft(projectId);
      if (message) {
        message.toRecipients = mailto.to.map((address) => ({
          emailAddress: { name: '', address },
        }));
        if (mailto.cc?.length) {
          message.ccRecipients = mailto.cc.map((address) => ({
            emailAddress: { name: '', address },
          }));
        }
        if (mailto.bcc?.length) {
          message.bccRecipients = mailto.bcc.map((address) => ({
            emailAddress: { name: '', address },
          }));
        }
        if (mailto.subject !== undefined) {
          message.subject = mailto.subject;
        }
        if (mailto.body) {
          const existingContent = message.body?.content ?? '';
          const newContent = escapeHtml(mailto.body).replace(
            /\r\n|\r|\n/g,
            '<br />'
          );
          message.body = {
            contentType: message.body?.contentType ?? 'html',
            content: existingContent
              ? `${newContent}<br />${existingContent}`
              : newContent,
          };
        }
        await apiUpdateMessageDraft(projectId, message.id, message);
        const width = window.screen.availWidth / 2;
        const height = window.screen.availHeight * 0.6;
        window.open(
          `/view/${projectId}/composer/${message.id}`,
          '_blank',
          `width=${width},height=${height},top=${height / 4}`
        );
      } else {
        notification.open({
          message: t('common:error'),
          description: t('mail:errorMessages.messages.createDraftError'),
        });
      }
    },
    [t]
  );
  //#endregion

  //#region ------------------------------ Ref
  useImperativeHandle(ref, () => ({
    getScrollWidth: () => {
      return internalRef.current?.scrollWidth ?? 0;
    },
    setScrollPosition: (value: { scrollLeft: number; scrollTop: number }) => {
      if (internalRef.current) {
        internalRef.current.scrollLeft = value.scrollLeft;
        internalRef.current.scrollTop = value.scrollTop;
      }
    },
  }));
  //#endregion

  //#region ------------------------------ Effects
  const messageBody = useMemo(() => {
    if (
      message?.body &&
      (message.body.contentType === 'html' ||
        message.body.contentType === 'Html' ||
        message.body.contentType === 1)
    ) {
      const sanitizedBody = sanitizeHTML(message.body.content);
      return ReactHtmlParser(sanitizedBody, {
        preprocessNodes(nodes) {
          nodes = manipulateDom(
            findBodyTagChildren(nodes) ?? nodes,
            bodyMaxWidth
          );
          const highlightedNodes = searchKeywords
            ? applySearch(nodes, searchKeywords)
            : nodes;
          return highlightedNodes;
        },
      });
    } else {
      let messageText = message?.body?.content ?? '';
      messageText = messageText.replace(/\n/g, '<br />');
      const sanitizedBody = sanitizeHTML(messageText);
      return ReactHtmlParser(sanitizedBody, {
        preprocessNodes(nodes) {
          nodes = manipulateDom(
            findBodyTagChildren(nodes) ?? nodes,
            bodyMaxWidth
          );
          const highlightedNodes = searchKeywords
            ? applySearch(nodes, searchKeywords)
            : nodes;
          return highlightedNodes;
        },
      });
    }
  }, [message, searchKeywords, bodyMaxWidth]);

  useEffect(() => {
    document?.querySelectorAll('#email-message-container a')?.forEach((el) => {
      el?.setAttribute('target', '_blank');
    });
  }, [message, searchKeywords, bodyMaxWidth]);

  useEffect(() => {
    const handleMailtoClick = (event: MouseEvent) => {
      // find the closest <a> up the tree, if any
      const anchor = (event.target as HTMLElement).closest(
        'a[href^="mailto:"]'
      ) as HTMLAnchorElement | null;

      if (!anchor) return;

      event.preventDefault();
      const mailto = parseMailto(anchor.href);
      openDraftMessage(projectId, mailto);
    };

    document.addEventListener('click', handleMailtoClick);
    return () => {
      document.removeEventListener('click', handleMailtoClick);
    };
  }, [openDraftMessage, projectId]);
  //#endregion

  return (
    <Flex.Column
      className={classNames(classes.root, className)}
      id="email-message-container"
    >
      <Flex.Item
        ref={(element) => {
          bodyRef(element);
          internalRef.current = element;
          if (element && element.children && element.children.length > 0) {
            try {
              debouncedScrollWidthChange(
                element.scrollWidth,
                element.offsetWidth,
                onScrollWidthChange
              );
            } catch (error) {
              console.error(error);
              debouncedScrollWidthChange(
                element.scrollWidth,
                element.offsetWidth,
                onScrollWidthChange
              );
            }
          }
        }}
      >
        {messageBody}
      </Flex.Item>
    </Flex.Column>
  );
});

export default HtmlTextContent;
